import type { CookieConsentStatus, CookieConsentValue } from "@/types/cookie-consent";

const STORAGE_KEY = "vntr18.cookie-consent";

/** Bump when the Cookie Policy changes materially so the banner re-prompts. */
export const COOKIE_POLICY_VERSION = "1.0";

/**
 * Read the stored cookie choice. Returns null when there is no valid choice
 * for the current policy version (so the banner shows again after a bump).
 */
export function readCookieConsent(): CookieConsentValue | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsentValue>;
    const validStatus = parsed.status === "accepted" || parsed.status === "necessary";
    if (!validStatus || parsed.version !== COOKIE_POLICY_VERSION) return null;
    return parsed as CookieConsentValue;
  } catch {
    return null;
  }
}

/** Persist the user's cookie choice for the current policy version. */
export function writeCookieConsent(status: CookieConsentStatus): void {
  const value: CookieConsentValue = {
    status,
    version: COOKIE_POLICY_VERSION,
    ts: Date.now(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage can throw in Safari private mode; the choice just won't persist.
  }
}

/**
 * Whether the user has consented to non-essential (analytics / marketing)
 * cookies. Gate any future tracker initialisation on this value.
 */
export function hasNonEssentialConsent(): boolean {
  return readCookieConsent()?.status === "accepted";
}
